import { t } from "@lingui/macro";
import type {
  CreateTimeTrackingItemMutationVariables,
  UpdateTimeTrackingItemMutationVariables,
} from "@src/__generated__/graphql";
import { toApiDate } from "@src/utils/dates";
import { formatDuration } from "@src/utils/formatters";
import { gt, lt, required } from "@src/utils/forms/validators";
import type { TaskOption } from "@src/widgets/TaskSelect/TaskSelect";
import { FieldState, FormState, FormStateLazy } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";
import { now } from "mobx-utils";

export class EntryForm {
  @observable.ref
  is_locked = false;

  fromLessThanTo = ($: number | undefined) => {
    if ($ === undefined) return null;
    if (this.specific_time_to.value === undefined) return null;
    return $ >= this.specific_time_to.value
      ? t`Can't be later than end time`
      : null;
  };
  toLessThanFrom = ($: number | undefined) => {
    if ($ === undefined) return null;
    if (this.specific_time_from.value === undefined) return null;
    return $ <= this.specific_time_from.value
      ? t`Can't be sooner than start time`
      : null;
  };
  requiredIfTimeToSpecified = ($: number | undefined) => {
    if (this.specific_time_to.value === undefined) return null;
    return required($);
  };

  @observable.ref selectedTask:
    | Pick<TaskOption, "value" | "label">
    | undefined = undefined;
  @observable timeTrackingSettings:
    | TaskOption["timeTrackingSettings"]
    | undefined = undefined;
  id = new FieldState("");
  project_id = new FieldState("").validators(required);
  budget_item_id = new FieldState("").validators(required);
  task_id = new FieldState("").validators(required);
  time_tracking_work_type_id = new FieldState("").validators(required);
  tracked_for_date = new FieldState(new Date(now())).validators(required);
  time = new FieldState<number | undefined>(undefined);
  notes = new FieldState("");
  billable = new FieldState(true);
  specificTimeEnabled = new FieldState(false);
  specific_time_from = new FieldState<number | undefined>(undefined).validators(
    gt(0),
    this.fromLessThanTo,
    this.requiredIfTimeToSpecified,
  );
  specific_time_to = new FieldState<number | undefined>(undefined).validators(
    gt(0),
    lt(86400, (time) => formatDuration(time)),
    this.toLessThanFrom,
  );
  capacity_allocation_item_id = new FieldState<string | undefined>(undefined);

  /** Proxy all fields to reset form */
  _form = new FormState([
    this.id,
    this.project_id,
    this.budget_item_id,
    this.task_id,
    this.time_tracking_work_type_id,
    this.tracked_for_date,
    this.time,
    this.specific_time_from,
    this.specific_time_to,
    this.specificTimeEnabled,
    this.capacity_allocation_item_id,
    this.notes,
    this.billable,
  ]);

  /** Proxy optional fields to validate form */
  _formLazy = new FormStateLazy(() => {
    return [
      this.project_id,
      this.budget_item_id,
      this.task_id,
      this.time_tracking_work_type_id,
      this.specific_time_from,
      this.specific_time_to,
      ...(this.id.value
        ? [
            this.tracked_for_date,
            this.time,
            this.specific_time_from,
            this.specific_time_to,
          ]
        : []),
    ];
  });

  constructor() {
    makeObservable(this);
  }

  @computed get editMode() {
    return !!this.id.value;
  }

  validate() {
    return this._formLazy.validate();
  }

  reset() {
    this.selectedTask = undefined;
    this.is_locked = false;
    this._form.reset();
  }

  @action.bound
  toggleSpecificTime(value: boolean) {
    this.specificTimeEnabled.onChange(value);
    if (value) {
      this.time.reset();
    } else {
      this.specific_time_from.reset();
      this.specific_time_to.reset();
    }
  }

  @action.bound
  serializeCreate(user_id: string): CreateTimeTrackingItemMutationVariables {
    return {
      user_id,
      task_id: this.task_id.value,
      time_tracking_work_type_id: this.time_tracking_work_type_id.value,
      tracked_time: this.specificTimeEnabled.value
        ? undefined
        : (this.time.value ?? 0),
      specificTimeFrom: this.specificTimeEnabled.value
        ? this.specific_time_from.value
          ? // NOTE: We have to add :00 (seconds) because GraphQL expects Time type
            `${formatDuration(this.specific_time_from.value)}:00`
          : null
        : null,
      specificTimeTo: this.specificTimeEnabled.value
        ? this.specific_time_to.value
          ? // NOTE: We have to add :00 (seconds) because GraphQL expects Time type
            `${formatDuration(this.specific_time_to.value)}:00`
          : null
        : null,
      tracked_for_date: toApiDate(this.tracked_for_date.value),
      note: this.notes.value,
      billable: this.billable.$,
      capacity_allocation_item_id: this.capacity_allocation_item_id.value,
    };
  }

  @action.bound
  serializeUpdate(user_id: string): UpdateTimeTrackingItemMutationVariables {
    const { tracked_time, ...rest } = this.serializeCreate(user_id);
    return { id: this.id.value, tracked_time: tracked_time!, ...rest };
  }
}
